import { Field } from '@/components/ui/field';
import { useBillingSettingsScope } from '@/hooks/actions/use-billing-settings-scope';
import { useOpenBillingPortal } from '@/hooks/actions/use-open-billing-portal';
import { useUpdateBillingContact } from '@/hooks/actions/use-update-billing-contact';
import {
  readBillingSettings,
  useBillingSettingsQuery,
} from '@/hooks/queries/use-billing-settings-query';
import SettingsSection from '@/pages/settings/components/SettingsSection';
import { Button, Input, Stack, Text } from '@chakra-ui/react';
import type { OrganizationBillingContext, OrganizationBillingSettingsData } from '@fluenceur/types';
import { NoticePanel } from '@fluenceur/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

function BillingContactEditor({
  snapshot,
  unavailable,
  mutationsDisabled,
  impersonating,
}: {
  snapshot: OrganizationBillingSettingsData;
  unavailable: boolean;
  mutationsDisabled: boolean;
  /** A support session reads billing but never changes it, whatever the account's role. */
  impersonating: boolean;
}) {
  const { t } = useTranslation();
  const update = useUpdateBillingContact(snapshot);
  const portal = useOpenBillingPortal(snapshot);
  const schema = z.object({
    billingEmail: z
      .string()
      .trim()
      .email(t('billing.settings.invalidEmail'))
      .max(254, t('validation.maxLength', { max: 254 })),
  });
  const form = useForm<{ billingEmail: string }>({
    resolver: zodResolver(schema),
    defaultValues: { billingEmail: snapshot.billingEmail ?? '' },
  });
  const { reset } = form;
  const dirty = form.formState.isDirty;
  useEffect(() => {
    if (!dirty) reset({ billingEmail: snapshot.billingEmail ?? '' });
  }, [snapshot.billingEmail, dirty, reset]);
  const busy = update.isPending || portal.isPending || form.formState.isSubmitting;
  const canManage = snapshot.canManage;
  const disabled = unavailable || mutationsDisabled || busy || !canManage;
  const readOnlyKey = impersonating ? 'billing.impersonationReadOnly' : 'billing.settings.readOnly';
  const submit = form.handleSubmit(async (request) => {
    if (disabled) return;
    try {
      const saved = await update.mutateAsync({
        expectedBillingEmail: snapshot.billingEmail,
        billingEmail: request.billingEmail,
      });
      reset({ billingEmail: saved.billingEmail ?? '' });
    } catch {
      // Keep the draft after a refusal or a concurrent update.
    }
  });
  return (
    <>
      <SettingsSection
        title={t('billing.settings.contactTitle')}
        description={t('billing.settings.contactDescription')}
      >
        <form noValidate onSubmit={submit}>
          <Stack gap={4} align="stretch">
            <Field
              label={t('billing.settings.emailLabel')}
              helperText={snapshot.closedAt ? t('billing.settings.closedReadOnly') : undefined}
              invalid={!!form.formState.errors.billingEmail}
              errorText={form.formState.errors.billingEmail?.message}
            >
              <Input
                {...form.register('billingEmail')}
                type="email"
                autoComplete="email"
                variant="outline"
                disabled={disabled}
              />
            </Field>
            {canManage ? (
              <Button
                type="submit"
                alignSelf="flex-start"
                loading={update.isPending}
                disabled={disabled || !dirty}
              >
                {t('common.save')}
              </Button>
            ) : (
              <Text color="fg.muted" fontSize="sm">
                {t(readOnlyKey)}
              </Text>
            )}
          </Stack>
        </form>
      </SettingsSection>
      <SettingsSection
        title={t('billing.settings.portalTitle')}
        description={t('billing.settings.portalDescription')}
      >
        {snapshot.canManage && snapshot.portalAvailable ? (
          <Button
            variant="outline"
            loading={portal.isPending}
            disabled={unavailable || mutationsDisabled || busy || !snapshot.canManage}
            onClick={() => void portal.mutateAsync().catch(() => undefined)}
          >
            {t('billing.settings.openPortal')}
          </Button>
        ) : (
          <Text fontSize="sm" color="fg.muted">
            {t(snapshot.canManage ? 'billing.settings.portalUnavailable' : readOnlyKey)}
          </Text>
        )}
      </SettingsSection>
    </>
  );
}

export function BillingSettingsSection({
  context,
  mutationsDisabled = false,
}: {
  context: OrganizationBillingContext;
  mutationsDisabled?: boolean;
}) {
  const { t } = useTranslation();
  const { scope } = useBillingSettingsScope();
  const query = useBillingSettingsQuery({ context });
  const settings = readBillingSettings(query.data);
  const scoped =
    !!scope.userId &&
    context.organizationId === scope.organizationId &&
    context.brandId === scope.brandId &&
    settings?.organizationId === scope.organizationId &&
    settings.brandId === scope.brandId;
  if (query.isPending && !query.data) return <Text>{t('common.loading')}</Text>;
  if (!scoped || !settings || query.error)
    return (
      <SettingsSection title={t('billing.settings.title')}>
        <NoticePanel
          tone="warning"
          title={t('billing.settings.readError')}
          actions={
            <Button variant="outline" onClick={() => void query.refetch()}>
              {t('common.retry')}
            </Button>
          }
        />
      </SettingsSection>
    );
  return (
    <BillingContactEditor
      key={`${scope.userId}:${scope.organizationId}:${scope.brandId}:${scope.role}:${scope.brandRole}`}
      snapshot={settings}
      unavailable={query.isFetching}
      mutationsDisabled={mutationsDisabled}
      impersonating={context.cardSetup.exemption === 'impersonation'}
    />
  );
}
